import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/authStore';
import FixCenter from '../components/FixCenter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MagnifyingGlass, ArrowLeft, Spinner, Copy, CheckCircle, WarningCircle,
  Info, ChartLineUp, Globe, Link as LinkIcon, FileText, Code, Lightbulb,
  ArrowSquareOut, CaretDown, CaretUp
} from '@phosphor-icons/react';

const C = {
  bg:'#0a0a0f', surface:'#13131f', deep:'#0d0d18', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', borderHi:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a',
  violet:'#7c3aed', blue:'#3b82f6',
  grad:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  green:'#10b981', red:'#f87171', amber:'#fbbf24',
};

export default function ReportPage() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedIssues, setExpandedIssues] = useState({});
  const [copiedSnippet, setCopiedSnippet] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { navigate('/login'); return; }
    const fetchReport = async () => {
      try {
        const data = await apiClient.get(`/reports/${analysisId}`);
        setReport(data);
      } catch (err) { setError(err.message || 'Failed to load report'); }
      finally { setLoading(false); }
    };
    if (isAuthenticated) fetchReport();
  }, [analysisId, isAuthenticated, authLoading, navigate]);

  const toggleIssue = (id) => setExpandedIssues(prev => ({ ...prev, [id]: !prev[id] }));

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSnippet(id);
      setTimeout(() => setCopiedSnippet(null), 2000);
    } catch (err) { console.error('Failed to copy:', err); }
  };

  const getScoreColor = (score) => score >= 80 ? C.green : score >= 50 ? C.amber : C.red;

  const getCWVStyle = (score) => {
    if (score === 'good') return { color:C.green, bg:'rgba(16,185,129,0.1)', border:'rgba(16,185,129,0.3)' };
    if (score === 'needs-improvement') return { color:C.amber, bg:'rgba(251,191,36,0.1)', border:'rgba(251,191,36,0.3)' };
    if (score === 'poor') return { color:C.red, bg:'rgba(248,113,113,0.1)', border:'rgba(248,113,113,0.3)' };
    return { color:C.muted, bg:C.surface, border:C.border };
  };

  const getSevColors = (severity) => {
    if (severity === 'critical') return { color:C.red, bg:'rgba(248,113,113,0.07)', border:'rgba(248,113,113,0.25)' };
    if (severity === 'warning') return { color:C.amber, bg:'rgba(251,191,36,0.07)', border:'rgba(251,191,36,0.25)' };
    if (severity === 'info') return { color:C.blue, bg:'rgba(59,130,246,0.07)', border:'rgba(59,130,246,0.25)' };
    return { color:C.muted, bg:C.deep, border:C.border };
  };

  const getSevIcon = (severity) => {
    if (severity === 'critical') return <WarningCircle weight="fill" style={{ width:18,height:18,color:C.red }} />;
    if (severity === 'warning') return <WarningCircle weight="fill" style={{ width:18,height:18,color:C.amber }} />;
    if (severity === 'info') return <Info weight="fill" style={{ width:18,height:18,color:C.blue }} />;
    return null;
  };

  if (authLoading || loading) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Spinner style={{ width:28, height:28, color:C.violet }} className="animate-spin" />
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <p style={{ color:C.red, marginBottom:16, fontSize:14 }}>{error}</p>
        <button onClick={() => navigate('/dashboard')}
          style={{ padding:'8px 18px', background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, cursor:'pointer', fontSize:13 }}>
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  if (!report) return null;

  const criticalCount = report.issues.filter(i => i.severity === 'critical').length;
  const warningCount = report.issues.filter(i => i.severity === 'warning').length;
  const infoCount = report.issues.filter(i => i.severity === 'info').length;

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:C.text }}>
      {/* Header */}
      <header style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none' }}>
            <div style={{ width:34, height:34, background:C.grad, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <MagnifyingGlass weight="bold" style={{ width:16, height:16, color:'#fff' }} />
            </div>
            <span style={{ fontSize:17, fontWeight:800, fontFamily:"'Satoshi', sans-serif", color:C.text, letterSpacing:'-0.02em' }}>SEO Dash 360</span>
          </Link>
          <div style={{ display:'flex', gap:10 }}>
            <button
              onClick={() => { const { accessToken: token } = useAuthStore.getState(); window.open(`https://seodash360.com/api/reports/${analysisId}/pdf?token=${token}`, '_blank'); }}
              style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'8px 18px', background:C.grad, border:'none', borderRadius:8, fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer', boxShadow:'0 2px 12px rgba(124,58,237,0.3)', fontFamily:'inherit' }}>
              <FileText style={{ width:15, height:15 }} /> Export PDF
            </button>
            <button onClick={() => navigate('/dashboard')}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.muted, cursor:'pointer', fontFamily:'inherit' }}>
              <ArrowLeft style={{ width:13, height:13 }} /> Dashboard
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth:1280, margin:'0 auto', padding:'32px 24px' }}>
        {/* Report header card */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:24, marginBottom:20 }}>
          <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'space-between', gap:24 }}>
            <div>
              <p style={{ fontSize:11, fontWeight:700, color:C.hint, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 6px' }}>SEO Report</p>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:22, fontWeight:900, color:C.text, margin:'0 0 6px', letterSpacing:'-0.02em' }}>
                {new URL(report.url).hostname}
              </h1>
              <a href={report.url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize:12, color:C.violet, fontFamily:"'JetBrains Mono', monospace", display:'inline-flex', alignItems:'center', gap:4, textDecoration:'none' }}>
                {report.url} <ArrowSquareOut style={{ width:12, height:12 }} />
              </a>
              <p style={{ fontSize:12, color:C.hint, margin:'6px 0 0' }}>Generated: {new Date(report.created_at).toLocaleString()}</p>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:20 }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:48, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color:getScoreColor(report.overall_score), letterSpacing:'-0.03em' }} data-testid="overall-score">
                  {report.overall_score}
                </div>
                <div style={{ fontSize:12, color:C.hint }}>Overall Score</div>
              </div>
              <div style={{ width:88, height:88, position:'relative' }}>
                <svg style={{ width:'100%', height:'100%', transform:'rotate(-90deg)' }} viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(124,58,237,0.15)" strokeWidth="8" />
                  <circle cx="50" cy="50" r="40" fill="none" stroke={getScoreColor(report.overall_score)} strokeWidth="8"
                    strokeLinecap="round" strokeDasharray={`${report.overall_score * 2.51} 251`} />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Score cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
          {[
            { label:'SEO', score:report.seo_score, icon:MagnifyingGlass },
            { label:'Performance', score:report.performance_score, icon:ChartLineUp },
            { label:'Accessibility', score:report.accessibility_score, icon:Globe },
            { label:'Best Practices', score:report.best_practices_score, icon:CheckCircle },
          ].map((item, i) => (
            <div key={i} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'20px 18px' }} data-testid={`score-${item.label.toLowerCase()}`}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                <item.icon style={{ width:16, height:16, color:C.hint }} />
                <span style={{ fontSize:13, color:C.muted }}>{item.label}</span>
              </div>
              <div style={{ fontSize:30, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color:getScoreColor(item.score) }}>{item.score}</div>
              <div style={{ height:3, background:'rgba(124,58,237,0.1)', borderRadius:2, marginTop:10 }}>
                <div style={{ height:'100%', width:`${item.score}%`, background:getScoreColor(item.score), borderRadius:2, transition:'width 0.6s ease' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="issues" className="space-y-4">
          <TabsList style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:4, height:'auto' }}>
            <TabsTrigger value="issues" className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white" style={{ fontSize:13, padding:'7px 18px', borderRadius:7, color:C.muted }}>
              Issues ({report.issues.length})
            </TabsTrigger>
            <TabsTrigger value="vitals" className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white" style={{ fontSize:13, padding:'7px 18px', borderRadius:7, color:C.muted }}>
              Core Web Vitals
            </TabsTrigger>
            <TabsTrigger value="meta" className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white" style={{ fontSize:13, padding:'7px 18px', borderRadius:7, color:C.muted }}>
              Meta & Structure
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white" style={{ fontSize:13, padding:'7px 18px', borderRadius:7, color:C.muted }}>
              Content
            </TabsTrigger>
          </TabsList>

          {/* Issues */}
          <TabsContent value="issues">
            <FixCenter report={report} analysisId={analysisId} />
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              {criticalCount > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:6 }}>
                  <WarningCircle weight="fill" style={{ width:14, height:14, color:C.red }} />
                  <span style={{ fontSize:12, fontWeight:600, color:C.red }}>{criticalCount} Critical</span>
                </div>
              )}
              {warningCount > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:6 }}>
                  <WarningCircle weight="fill" style={{ width:14, height:14, color:C.amber }} />
                  <span style={{ fontSize:12, fontWeight:600, color:C.amber }}>{warningCount} Warnings</span>
                </div>
              )}
              {infoCount > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:6 }}>
                  <Info weight="fill" style={{ width:14, height:14, color:C.blue }} />
                  <span style={{ fontSize:12, fontWeight:600, color:C.blue }}>{infoCount} Info</span>
                </div>
              )}
            </div>

            {report.issues.length === 0 ? (
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'48px 24px', textAlign:'center' }}>
                <CheckCircle weight="fill" style={{ width:44, height:44, color:C.green, margin:'0 auto 12px' }} />
                <h3 style={{ fontSize:16, fontWeight:700, color:C.text, margin:'0 0 6px' }}>No issues found!</h3>
                <p style={{ fontSize:13, color:C.muted, margin:0 }}>Your website is following SEO best practices.</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {report.issues.map(issue => {
                  const sc = getSevColors(issue.severity);
                  return (
                    <div key={issue.id} style={{ background:C.deep, border:`1px solid ${sc.border}`, borderRadius:10, overflow:'hidden' }} data-testid={`issue-${issue.id}`}>
                      <button onClick={() => toggleIssue(issue.id)}
                        style={{ width:'100%', padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', background:'transparent', border:'none', cursor:'pointer', textAlign:'left' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                          {getSevIcon(issue.severity)}
                          <div>
                            <h3 style={{ fontSize:14, fontWeight:600, color:C.text, margin:'0 0 2px' }}>{issue.title}</h3>
                            <p style={{ fontSize:12, color:C.hint, margin:0 }}>{issue.category}</p>
                          </div>
                        </div>
                        {expandedIssues[issue.id]
                          ? <CaretUp style={{ width:16, height:16, color:C.hint }} />
                          : <CaretDown style={{ width:16, height:16, color:C.hint }} />}
                      </button>
                      {expandedIssues[issue.id] && (
                        <div style={{ padding:'0 18px 18px', borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
                          <p style={{ fontSize:13, color:C.muted, marginBottom:14 }}>{issue.description}</p>
                          {issue.fix_suggestion && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                                <Lightbulb style={{ width:14, height:14, color:C.violet }} />
                                <span style={{ fontSize:12, fontWeight:600, color:C.text }}>Fix Suggestion</span>
                              </div>
                              <p style={{ fontSize:13, color:C.muted, background:C.surface, padding:'10px 12px', borderRadius:7, margin:0 }}>{issue.fix_suggestion}</p>
                            </div>
                          )}
                          {issue.code_snippet && (
                            <div>
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                  <Code style={{ width:14, height:14, color:C.violet }} />
                                  <span style={{ fontSize:12, fontWeight:600, color:C.text }}>Code Snippet</span>
                                </div>
                                <button onClick={() => copyToClipboard(issue.code_snippet, issue.id)}
                                  style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px', background:C.surface, border:`1px solid ${C.border}`, borderRadius:5, fontSize:12, color:copiedSnippet===issue.id?C.green:C.muted, cursor:'pointer' }}
                                  data-testid={`copy-snippet-${issue.id}`}>
                                  {copiedSnippet===issue.id ? <><CheckCircle style={{ width:12,height:12 }} /> Copied!</> : <><Copy style={{ width:12,height:12 }} /> Copy</>}
                                </button>
                              </div>
                              <pre style={{ background:C.input, borderRadius:7, padding:'12px 14px', fontSize:12, fontFamily:"'JetBrains Mono', monospace", color:'#a3e635', overflow:'auto', margin:0, lineHeight:1.6 }}>
                                <code>{issue.code_snippet}</code>
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Core Web Vitals */}
          <TabsContent value="vitals">
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24 }}>
              <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:C.text, margin:'0 0 20px' }}>Core Web Vitals</h2>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                {[
                  { label:'Largest Contentful Paint (LCP)', value:`${report.core_web_vitals.lcp}s`, score:report.core_web_vitals.lcp_score, target:'≤ 2.5s', id:'lcp' },
                  { label:'First Input Delay (FID)', value:`${report.core_web_vitals.fid}ms`, score:report.core_web_vitals.fid_score, target:'≤ 100ms', id:'fid' },
                  { label:'Cumulative Layout Shift (CLS)', value:report.core_web_vitals.cls, score:report.core_web_vitals.cls_score, target:'≤ 0.1', id:'cls' },
                ].map(vital => {
                  const vc = getCWVStyle(vital.score);
                  return (
                    <div key={vital.id} style={{ border:`1px solid ${vc.border}`, background:vc.bg, borderRadius:10, padding:20 }}>
                      <div style={{ fontSize:12, color:C.muted, marginBottom:8 }}>{vital.label}</div>
                      <div style={{ fontSize:28, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color:vc.color, marginBottom:8 }} data-testid={`${vital.id}-value`}>{vital.value}</div>
                      <div style={{ display:'inline-block', padding:'3px 10px', borderRadius:5, fontSize:11, fontWeight:700, background:vc.bg, color:vc.color, border:`1px solid ${vc.border}` }}>
                        {vital.score.replace('-', ' ')}
                      </div>
                      <p style={{ fontSize:12, color:C.hint, margin:'10px 0 0' }}>Target: {vital.target}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Meta & Structure */}
          <TabsContent value="meta">
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24, display:'flex', flexDirection:'column', gap:14 }}>
              <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:C.text, margin:0 }}>Meta & Structure Analysis</h2>
              <div style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:C.text }}>Page Title</span>
                  <span style={{ fontSize:12, color:C.hint, fontFamily:"'JetBrains Mono', monospace" }}>{report.meta_analysis.title_length} chars</span>
                </div>
                <p style={{ fontSize:13, color:C.muted, background:C.input, padding:'10px 12px', borderRadius:7, margin:0, fontFamily:"'JetBrains Mono', monospace" }} data-testid="meta-title">
                  {report.meta_analysis.title || 'Not found'}
                </p>
              </div>
              <div style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:C.text }}>Meta Description</span>
                  <span style={{ fontSize:12, color:C.hint, fontFamily:"'JetBrains Mono', monospace" }}>{report.meta_analysis.meta_description_length} chars</span>
                </div>
                <p style={{ fontSize:13, color:C.muted, background:C.input, padding:'10px 12px', borderRadius:7, margin:0 }} data-testid="meta-description">
                  {report.meta_analysis.meta_description || 'Not found'}
                </p>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {[
                  { label:`H1 Tags (${report.meta_analysis.h1_tags.length})`, tags:report.meta_analysis.h1_tags },
                  { label:`H2 Tags (${report.meta_analysis.h2_tags.length})`, tags:report.meta_analysis.h2_tags },
                ].map(h => (
                  <div key={h.label} style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:C.text, display:'block', marginBottom:8 }}>{h.label}</span>
                    {h.tags.length > 0 ? (
                      <ul style={{ margin:0, padding:0, listStyle:'none', maxHeight:120, overflowY:'auto', display:'flex', flexDirection:'column', gap:4 }}>
                        {h.tags.map((tag, i) => <li key={i} style={{ fontSize:12, color:C.muted, background:C.input, padding:'6px 10px', borderRadius:6 }}>{tag}</li>)}
                      </ul>
                    ) : <p style={{ fontSize:12, color:C.hint, margin:0 }}>No tags found</p>}
                  </div>
                ))}
              </div>
              <div style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:C.text }}>Schema.org Markup</span>
                  {report.meta_analysis.has_schema
                    ? <span style={{ fontSize:12, color:C.green, display:'flex', alignItems:'center', gap:4 }}><CheckCircle weight="fill" style={{ width:14,height:14 }} /> Detected</span>
                    : <span style={{ fontSize:12, color:C.red, display:'flex', alignItems:'center', gap:4 }}><WarningCircle weight="fill" style={{ width:14,height:14 }} /> Not found</span>}
                </div>
                {report.meta_analysis.schema_types.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {report.meta_analysis.schema_types.map((type, i) => (
                      <span key={i} style={{ padding:'3px 10px', background:'rgba(124,58,237,0.12)', color:C.violet, fontSize:12, borderRadius:5, fontFamily:"'JetBrains Mono', monospace" }}>{type}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {[
                  { label:'Internal Links', icon:LinkIcon, value:report.link_analysis.internal_links },
                  { label:'External Links', icon:ArrowSquareOut, value:report.link_analysis.external_links },
                ].map(link => (
                  <div key={link.label} style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                      <link.icon style={{ width:14, height:14, color:C.hint }} />
                      <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{link.label}</span>
                    </div>
                    <div style={{ fontSize:26, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color:C.blue }}>{link.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Content */}
          <TabsContent value="content">
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24, display:'flex', flexDirection:'column', gap:14 }}>
              <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:C.text, margin:0 }}>Content Analysis</h2>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                {[
                  { label:'Word Count', value:report.content_analysis.word_count.toLocaleString(), color:C.text, id:'word-count' },
                  { label:'Readability Score', value:report.content_analysis.readability_score, color:getScoreColor(report.content_analysis.readability_score) },
                  { label:'Semantic Relevance', value:`${Math.round(report.content_analysis.semantic_relevance * 100)}%`, color:getScoreColor(report.content_analysis.semantic_relevance * 100) },
                ].map(stat => (
                  <div key={stat.label} style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                    <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>{stat.label}</div>
                    <div style={{ fontSize:24, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color:stat.color }} data-testid={stat.id}>{stat.value}</div>
                  </div>
                ))}
              </div>
              {Object.keys(report.content_analysis.keyword_density).length > 0 && (
                <div style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                  <h3 style={{ fontSize:13, fontWeight:700, color:C.text, margin:'0 0 12px' }}>Keyword Density</h3>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {Object.entries(report.content_analysis.keyword_density).map(([kw, density]) => (
                      <div key={kw} style={{ display:'flex', justifyContent:'space-between' }}>
                        <span style={{ fontSize:13, color:C.muted }}>{kw}</span>
                        <span style={{ fontSize:12, color:C.text, fontFamily:"'JetBrains Mono', monospace" }}>{density}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {report.content_analysis.suggested_keywords.length > 0 && (
                <div style={{ border:`1px solid ${C.border}`, borderRadius:9, padding:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12 }}>
                    <Lightbulb style={{ width:14, height:14, color:C.violet }} />
                    <h3 style={{ fontSize:13, fontWeight:700, color:C.text, margin:0 }}>AI Suggested Keywords</h3>
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {report.content_analysis.suggested_keywords.map((kw, i) => (
                      <span key={i} style={{ padding:'4px 12px', background:C.input, border:`1px solid ${C.border}`, fontSize:12, color:C.muted, borderRadius:6 }}>{kw}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
